import { appMode, getKeyValue, loggedIn, ModalDialog, Stage, stage, stateOpenModal, storeKeyValue } from '@infinitro/global';
import { writable } from 'svelte/store';
export const app = import.meta.env.VITE_APP_NAME?.split('/').pop();
export const version = import.meta.env.VITE_APP_VERSION;
export const build = Number(version?.split('.')?.pop() ?? 0);
export const currentVersion = writable(version);
export const updateVersion = writable('');
let newBuild = -1;
let newVersion = '';
let isLoggedIn = undefined;
loggedIn.subscribe((value) => {
    isLoggedIn = value;
});
const INITIAL_DELAY = stage !== Stage.Prod ? 100 : 200; // (initial delay before checking for updates)
const FREQUENCY = stage !== Stage.Prod ? 1000 * 15 : 1000 * 60; // (how often to check for updates)
const TIMEOUT = stage !== Stage.Prod ? 1000 * 60 * 10 : 1000 * 60 * 60; // (timeout before allowing another attempt or asking again)
const DELAY = stage != Stage.Prod ? 1000 * 5 : 1000 * 30; // (delay before notifying, allows time for Cloudfront to invalidate)
const sleep = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));
const shouldUpdate = async () => {
    try {
        if (!app || !version || !build)
            return false;
        if (newBuild <= 0)
            return false;
        if (build === newBuild)
            return false;
        // May need to change build > newBuild behavior to permit rollbacks, but we don't
        // want version flipping. Rollbacks may be better implemented via a notification mechanism.
        if (build > newBuild)
            return false;
        const lastUpgradeAttemptBuild = await getKeyValue(`${app}:lastUpgradeAttemptBuild`);
        const lastUpgradeAttemptDate = await getKeyValue(`${app}:lastUpgradeAttemptDate`);
        if (lastUpgradeAttemptBuild &&
            lastUpgradeAttemptDate &&
            Number(lastUpgradeAttemptBuild) >= newBuild) {
            // prevent another upgrade retry attempt until timeout expires
            if (Number(lastUpgradeAttemptDate) > new Date().getTime() - TIMEOUT) {
                // console.log(
                // 	`will retry again in ${Math.floor((TIMEOUT + Number(lastUpgradeAttemptDate) - new Date().getTime()) / 1000 / 60)} minutes`
                // );
                return false;
            }
        }
        return true;
    }
    catch (e) {
        console.log(`error evaluating shouldUpdate: ${e}`);
        return false;
    }
};
const getNewBuild = async () => {
    try {
        if (window?.location?.hostname?.includes('localhost'))
            return;
        const res = await fetch((isLoggedIn ? `/${appMode}/version.json?` : '/version.json?') +
            new Date().getTime().toString());
        const ver = await res.json();
        newVersion = ver?.version ?? '';
        newBuild = Number(ver?.version?.split('.')?.pop() ?? 0);
        updateVersion.set(newVersion);
    }
    catch {
        // empty // may have network error, do not log
    }
};
const notifyUpdateAvailable = async () => {
    stateOpenModal.set(ModalDialog.Update);
};
export const doUpdate = async () => {
    try {
        console.log(`attempting update from ${build} to ${newBuild}...`);
        // store update attempt date so we can implement a retry timeout
        await storeKeyValue(`${app}:lastUpgradeAttemptBuild`, newBuild.toString());
        await storeKeyValue(`${app}:lastUpgradeAttemptDate`, new Date().getTime().toString());
        // do upgrade
        if ('caches' in window) {
            try {
                // Clear all caches
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
                // Reload the page
                // @ts-expect-error(TS2339)
                window.location.reload(true);
            }
            catch (err) {
                console.log('Cache clearing failed:', err);
                // Fallback to simple reload
                // @ts-expect-error(TS2339)
                window.location.reload(true);
            }
        }
        else {
            // Fallback for browsers without Cache API support
            // @ts-expect-error(TS2339)
            window.location.reload(true);
        }
    }
    catch (e) {
        console.log(`error attempting update: ${e}`);
    }
};
export const dismissUpdate = async () => {
    await storeKeyValue(`${app}:lastUpgradeAttemptBuild`, newBuild.toString());
    await storeKeyValue(`${app}:lastUpgradeAttemptDate`, new Date().getTime().toString());
    console.log(`dismissed ${stage} update`);
};
const checkForUpdates = async () => {
    if (isLoggedIn === undefined)
        return;
    await getNewBuild();
    if (await shouldUpdate()) {
        await sleep(DELAY);
        await notifyUpdateAvailable();
    }
};
setTimeout(async () => await checkForUpdates(), INITIAL_DELAY);
setInterval(async () => await checkForUpdates(), FREQUENCY);
